import { ApplicationRef, inject, Injectable, Injector } from '@angular/core';
import { MonoTypeOperatorFunction, tap } from 'rxjs';

/**
 * Bridges async work that completes outside Angular's notification mechanisms
 * (Firebase SDK callbacks, promise chains) into zoneless change detection.
 *
 * With zone.js removed, Angular no longer schedules change detection after
 * arbitrary async callbacks. Components that subscribe to the shared services
 * and assign plain fields would render stale. The shared services route every
 * emission through notify(), which marks the root views dirty so the zoneless
 * scheduler runs a (coalesced) change-detection pass — the same effect zone.js
 * had, scoped to the service layer instead of every async API on the page.
 *
 * New code should prefer signals or the async pipe over relying on this.
 */
@Injectable({
  providedIn: 'root',
})
export class ChangeNotifierService {
  // ApplicationRef is resolved lazily: this service is pulled in by services
  // that are instantiated during app initializers, before ApplicationRef may
  // be injected.
  private injector = inject(Injector);

  /** Schedule a change-detection pass for all root views. */
  notify(): void {
    for (const componentRef of this.injector.get(ApplicationRef).components) {
      componentRef.changeDetectorRef.markForCheck();
    }
  }

  /** Schedule a change-detection pass on every emission, error or completion. */
  notifyOnEmit<T>(): MonoTypeOperatorFunction<T> {
    return tap<T>({
      next: () => this.notify(),
      error: () => this.notify(),
      complete: () => this.notify(),
    });
  }

  /**
   * Schedule a change-detection pass when the promise settles. The pass runs
   * after the caller's own then/catch callbacks, so state they assign is
   * rendered too.
   */
  notifyWhenSettled<T>(promise: Promise<T>): Promise<T> {
    return promise.finally(() => this.notify());
  }
}
